import api from "./api";

const authService = {
  /**
   * Login with email and password
   */
  login: async (credentials) => {
    const response = await api.post("/auth/login", credentials);
    return response.data;
  },

  /**
   * Initiate registration - sends verification code to email
   * Step 1 of 2-step registration process
   */
  initiateRegistration: async (email) => {
    const response = await api.post("/auth/register", { email });
    return response.data;
  },

  /**
   * Complete registration - verify code and create user
   * Step 2 of 2-step registration process
   */
  verifyRegistration: async ({
    email,
    code,
    password,
    name,
    role,
    businessName,
  }) => {
    const response = await api.post("/auth/verify-registration", {
      email,
      code,
      password,
      name,
      role,
      ...(businessName && { businessName }),
    });
    return response.data;
  },

  /**
   * Resend registration verification code
   */
  resendRegistrationVerification: async (email) => {
    const response = await api.post("/auth/resend-registration-verification", {
      email,
    });
    return response.data;
  },

  /**
   * Logout user
   */
  logout: async (refreshToken) => {
    const response = await api.post("/auth/logout", { refreshToken });
    return response.data;
  },

  /**
   * Request password reset
   */
  forgotPassword: async (email) => {
    const response = await api.post("/auth/forgot-password", { email });
    return response.data;
  },

  /**
   * Reset password with OTP
   */
  resetPassword: async ({ email, code, password }) => {
    const response = await api.post("/auth/reset-password", {
      email,
      code,
      password,
    });
    return response.data;
  },

  /**
   * Verify email with code
   */
  verifyEmail: async (code) => {
    const response = await api.post("/auth/verify-email", { code });
    return response.data;
  },

  /**
   * Resend verification email
   */
  sendVerificationEmail: async () => {
    const response = await api.post("/auth/send-verification-email");
    return response.data;
  },

  /**
   * Get current user profile
   */
  getCurrentUser: async () => {
    const response = await api.get("/users/profile");
    return response.data;
  },

  /**
   * Update user profile
   */
  updateProfile: async (data) => {
    const response = await api.patch("/users/profile", data);
    return response.data;
  },

  /**
   * Update user password
   */
  updatePassword: async (data) => {
    const response = await api.post("/auth/update-password", data);
    return response.data; // Will be empty for 204, but handle as needed
  },

  /**
   * Upgrade homeowner to service provider
   */
  upgradeToProvider: async (data) => {
    const response = await api.post("/users/upgrade-to-provider", data);
    return response.data;
  },

  /**
   * Send phone verification code
   */
  sendPhoneVerification: async () => {
    const response = await api.post("/auth/send-phone-verification");
    return response.data;
  },

  /**
   * Verify phone number
   */
  verifyPhone: async (code) => {
    const response = await api.post("/auth/verify-phone", { code });
    return response.data;
  },

  /**
   * Unlink OAuth provider from account
   */
  unlinkOAuth: async (provider) => {
    const response = await api.delete("/auth/unlink-oauth", {
      data: { provider },
    });
    return response.data;
  },

  /**
   * Create a link session for OAuth account linking
   * Returns a short-lived linkToken to use in the OAuth redirect
   */
  createGoogleLinkSession: async () => {
    const response = await api.post("/auth/google/link-session");
    return response.data;
  },

  // ==================== EMAIL CHANGE FLOW ====================

  /**
   * Initiate email change - sends verification code to NEW email
   */
  initiateEmailChange: async (email) => {
    const response = await api.post("/users/email", { email });
    return response.data;
  },

  /**
   * Verify email change with code
   */
  verifyEmailChange: async (code) => {
    const response = await api.post("/users/email/verify", { code });
    return response.data;
  },

  /**
   * Resend email change verification code
   */
  resendEmailChangeCode: async () => {
    const response = await api.post("/users/email/resend");
    return response.data;
  },

  /**
   * Cancel pending email change
   */
  cancelEmailChange: async () => {
    const response = await api.delete("/users/email/pending");
    return response.data;
  },

  // ==================== PHONE CHANGE FLOW ====================

  /**
   * Initiate phone change - sends verification code via SMS
   */
  initiatePhoneChange: async (phone) => {
    const response = await api.post("/users/phone", { phone });
    return response.data;
  },

  /**
   * Verify phone change with code
   */
  verifyPhoneChange: async (code) => {
    const response = await api.post("/users/phone/verify", { code });
    return response.data;
  },

  /**
   * Resend phone change verification code
   */
  resendPhoneChangeCode: async () => {
    const response = await api.post("/users/phone/resend");
    return response.data;
  },

  /**
   * Cancel pending phone change
   */
  cancelPhoneChange: async () => {
    const response = await api.delete("/users/phone/pending");
    return response.data;
  },
};

export default authService;
